import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, Truck, Package, CheckCircle, Clock, Send, FileSpreadsheet, ArrowUpDown, ChevronUp, ChevronDown, Loader2, Edit2, Save, X } from 'lucide-react';
import { fetchDeliveries, createDelivery, fetchOrders, updateDelivery } from '../services/api';
import * as XLSX from 'xlsx';

const Deliveries = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [filteredDeliveries, setFilteredDeliveries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'order_number', direction: 'asc' });
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadDeliveries();
  }, []);

  useEffect(() => {
    filterAndSortDeliveries();
  }, [deliveries, searchTerm, statusFilter, sortConfig]);

  const loadDeliveries = async () => {
    try {
      const data = await fetchDeliveries();
      setDeliveries(data);
    } catch (error) {
      console.error('Failed to load deliveries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (delivery) => {
    setEditingId(delivery.id);
    setEditValue(delivery.notes || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleSaveNote = async (deliveryId) => {
    try {
      const timestamp = new Date().toLocaleString();
      const updatedNote = `${editValue}\n[Edited on ${timestamp}]`;
      
      await updateDelivery(deliveryId, { notes: updatedNote });
      
      setDeliveries(prev => prev.map(d => 
        d.id === deliveryId ? { ...d, notes: updatedNote } : d
      ));
      setEditingId(null);
    } catch (error) {
      console.error('Failed to update note:', error);
      alert('Failed to save note. Please try again.');
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        await processImport(jsonData);
        alert('Data imported successfully!');
        loadDeliveries();
      } catch (error) {
        console.error('Import failed:', error);
        alert(`Import failed: ${error.message}`);
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const processImport = async (rows) => {
    if (rows.length < 2) throw new Error('Excel file is empty or missing headers');

    const headers = rows[0];
    const headerMap = {
      'Order #': 'order_number',
      'Country': 'country',
      'Invoice #': 'invoice_no',
      'Carrier': 'carrier',
      'Tracking Number': 'tracking_number',
      'Tracking #': 'tracking_number',
      'Status': 'status',
      'Issue': 'issue',
      'Last Update': 'last_update',
      'Notes': 'notes',
      'Notes/Details': 'notes'
    };

    const mappedHeaders = headers.map(h => headerMap[h] || String(h).toLowerCase().replace(/[^a-z0-9]/g, '_'));

    // Fetch all orders to map order_number -> order_id
    const orders = await fetchOrders();
    const orderMap = {};
    orders.forEach(o => {
      orderMap[o.order_number] = o.id;
      if (o.order_number.startsWith('#')) {
        orderMap[o.order_number.substring(1)] = o.id;
      }
    });

    const dataRows = rows.slice(1);
    let successCount = 0;
    let errorCount = 0;

    for (const values of dataRows) {
      if (values.length === 0) continue;
      
      const row = {};
      mappedHeaders.forEach((header, index) => {
        row[header] = values[index];
      });

      if (!row.order_number) continue;

      // Clean order number for lookup
      let orderNum = String(row.order_number);
      if (orderNum.startsWith('#')) orderNum = orderNum.substring(1);
      
      const orderId = orderMap[row.order_number] || orderMap[orderNum];

      if (orderId) {
        try {
          await createDelivery({
            order_id: orderId,
            carrier: row.carrier,
            tracking_number: row.tracking_number,
            status: String(row.status || '').toLowerCase().replace(' ', '_') || 'pending',
            invoice_no: row.invoice_no,
            issue: row.issue,
            notes: row.notes,
            last_update: row.last_update,
            shipping_address: { country: row.country }
          });
          successCount++;
        } catch (err) {
          console.error(`Failed to import row for order ${row.order_number}:`, err);
          errorCount++;
        }
      } else {
        console.warn(`Order number not found: ${row.order_number}`);
        errorCount++;
      }
    }

    if (errorCount > 0) {
      throw new Error(`Imported ${successCount} rows. ${errorCount} rows failed (likely order numbers not found).`);
    }
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={14} className="text-gray-400" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronUp size={14} className="text-primary-600" /> : 
      <ChevronDown size={14} className="text-primary-600" />;
  };

  const filterAndSortDeliveries = () => {
    let result = [...deliveries];

    // Filter
    if (searchTerm) {
      result = result.filter(delivery =>
        delivery.tracking_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        delivery.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        delivery.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        delivery.invoice_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        delivery.country?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(delivery => delivery.status === statusFilter);
    }

    // Sort
    result.sort((a, b) => {
      const aValue = a[sortConfig.key] || '';
      const bValue = b[sortConfig.key] || '';

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    setFilteredDeliveries(result);
  };

  const handleResendTracking = async (delivery) => {
    try {
      console.log(`Resending tracking for Order #${delivery.order_number} to ${delivery.customer_name}`);
      alert(`Tracking info for Order #${delivery.order_number} has been resent to the customer.`);
    } catch (error) {
      console.error('Failed to resend tracking:', error);
      alert('Failed to resend tracking info. Please try again.');
    }
  };

  const downloadExcel = () => {
    const headers = ['Order #', 'Country', 'Invoice #', 'Carrier', 'Tracking Number', 'Status', 'Issue', 'Last Update', 'Notes'];
    const data = filteredDeliveries.map(d => [
      d.order_number,
      d.country || '',
      d.invoice_no || '',
      d.carrier || '',
      d.tracking_number || '',
      d.status,
      d.issue || '',
      d.last_update || '',
      d.notes || ''
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Deliveries');
    
    XLSX.writeFile(workbook, `deliveries_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_transit: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      delayed: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading deliveries...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Deliveries</h1>
        <div className="flex items-center gap-3">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".xlsx, .xls"
            className="hidden"
          />
          <button 
            onClick={handleImportClick}
            disabled={importing}
            className="btn-secondary flex items-center gap-2"
          >
            {importing ? <Loader2 size={20} className="animate-spin" /> : <FileSpreadsheet size={20} />}
            {importing ? 'Importing...' : 'Import Excel'}
          </button>
          <button 
            onClick={downloadExcel}
            className="btn-primary flex items-center gap-2"
          >
            <FileSpreadsheet size={20} />
            Export Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by tracking, order, invoice, country..."
              className="input-field pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="input-field"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="in_transit">In Transit</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
            <option value="delayed">Delayed</option>
          </select>
        </div>
      </div>

      {/* Deliveries Table (Excel Form) */}
      <div className="card overflow-x-auto p-0 border border-gray-300">
        {filteredDeliveries.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No deliveries found.
          </div>
        ) : (
          <table className="w-full border-collapse min-w-[1200px]">
            <thead>
              <tr className="bg-gray-200 border-b border-gray-300">
                <th 
                  className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('order_number')}
                >
                  <div className="flex items-center justify-between">
                    Order # {getSortIcon('order_number')}
                  </div>
                </th>
                <th 
                  className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('country')}
                >
                  <div className="flex items-center justify-between">
                    Country {getSortIcon('country')}
                  </div>
                </th>
                <th 
                  className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('invoice_no')}
                >
                  <div className="flex items-center justify-between">
                    Invoice # {getSortIcon('invoice_no')}
                  </div>
                </th>
                <th 
                  className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('carrier')}
                >
                  <div className="flex items-center justify-between">
                    Carrier {getSortIcon('carrier')}
                  </div>
                </th>
                <th 
                  className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('tracking_number')}
                >
                  <div className="flex items-center justify-between">
                    Tracking # {getSortIcon('tracking_number')}
                  </div>
                </th>
                <th 
                  className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('status')}
                >
                  <div className="flex items-center justify-between">
                    Status {getSortIcon('status')}
                  </div>
                </th>
                <th 
                  className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('issue')}
                >
                  <div className="flex items-center justify-between">
                    Issue {getSortIcon('issue')}
                  </div>
                </th>
                <th 
                  className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('last_update')}
                >
                  <div className="flex items-center justify-between">
                    Last Update {getSortIcon('last_update')}
                  </div>
                </th>
                <th 
                  className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600 cursor-pointer hover:bg-gray-300"
                  onClick={() => requestSort('notes')}
                >
                  <div className="flex items-center justify-between">
                    Notes/Details {getSortIcon('notes')}
                  </div>
                </th>
                <th className="text-left py-2 px-4 font-bold text-xs uppercase tracking-wider text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeliveries.map((delivery) => (
                <tr key={delivery.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                  <td className="py-1.5 px-4 border-r border-gray-200 font-medium text-sm">
                    <Link to={`/orders/${delivery.order_id}`} className="text-primary-600 hover:underline">
                      #{delivery.order_number}
                    </Link>
                  </td>
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-600">{delivery.country}</td>
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-600">{delivery.invoice_no}</td>
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-600">{delivery.carrier}</td>
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm font-mono text-gray-600">{delivery.tracking_number}</td>
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(delivery.status)}`}>
                      {delivery.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-red-600 font-medium">{delivery.issue}</td>
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-500 whitespace-nowrap">
                    {delivery.last_update}
                  </td>
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-600 min-w-[200px]">
                    {editingId === delivery.id ? (
                      <div className="flex flex-col gap-2 py-1">
                        <textarea
                          className="input-field text-xs min-h-[60px]"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveNote(delivery.id)}
                            className="text-green-600 hover:text-green-700 flex items-center gap-1"
                          >
                            <Save size={14} /> Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="text-gray-500 hover:text-gray-600 flex items-center gap-1"
                          >
                            <X size={14} /> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between group">
                        <div className="max-w-[150px] truncate" title={delivery.notes}>
                          {delivery.notes}
                        </div>
                        <button
                          onClick={() => handleStartEdit(delivery)}
                          className="text-primary-600 hover:text-primary-700 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Edit note"
                        >
                          <Edit2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 px-4 text-sm">
                    <button
                      onClick={() => handleResendTracking(delivery)}
                      className="flex items-center gap-1.5 text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
                      title="Resend tracking to customer"
                    >
                      <Send size={14} />
                      Resend Tracking
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Deliveries;